<!-- 
Dialog for editing settings for a user's dashboard.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg">
    <v-toolbar color="secondary" flat dark>
      <v-toolbar-title>Dashboard settings</v-toolbar-title>
      <v-spacer/>
      <our-user-guide-button topic="levy-collection" section="dashboard"/>
    </v-toolbar>
    <v-card flat>
      <v-card-text style="max-height: 380px; overflow-y: auto;">
        <v-slide-y-transition 
          group
          tag="v-list"
          class="v-list v-list--dense v-list--subheader v-list--three-line theme--light"
        >
          <v-subheader key="included">
            <div class="d-flex" style="width: 100%">
              <span class="d-inline-flex">Included</span>
              <span class="d-inline-flex justify-end">Order</span>
            </div>
          </v-subheader>
          <v-list-tile v-for="(widget, index) in includedWidgets" :key="widget.component">
            <v-list-tile-action>
              <v-switch :input-value="widget.included" color="primary" @change="toggleIncluded(widget.component)" />
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{ widget.name }}</v-list-tile-title>
              <v-list-tile-sub-title>{{ widget.description }}</v-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-action style="width: 120px;">
              <div class="d-inline-block">
                <v-btn 
                  :disabled="index == 0" flat icon class="mr-2" 
                  @click="up(widget.component)"
                >
                  <v-icon class="subheading">arrow_upward</v-icon>
                </v-btn>
                <v-btn 
                  :disabled="index == includedWidgets.length - 1" flat icon 
                  @click="down(widget.component)"
                >
                  <v-icon class="subheading">arrow_downward</v-icon>
                </v-btn>
              </div>
            </v-list-tile-action>
          </v-list-tile>
          <v-subheader v-if="availableWidgets.length > 0" key="available">Available</v-subheader>
          <v-list-tile v-for="(widget) in availableWidgets" :key="widget.component">
            <v-list-tile-action>
              <v-switch :input-value="widget.included" color="primary" @change="toggleIncluded(widget.component)" />
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{ widget.name }}</v-list-tile-title>
              <v-list-tile-sub-title>{{ widget.description }}</v-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-action style="width: 120px;">
              &nbsp;
            </v-list-tile-action>
          </v-list-tile>
        </v-slide-y-transition>
      </v-card-text>
      <v-card-actions>
        <v-btn id="dashboard-reset" flat color="error" @click="reset">
          Reset to default
        </v-btn>
        <v-spacer/>
        <v-btn flat color="primary" @click.native="hide">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from "underscore";
import { dashboardHelper } from "@/store/users/helpers";
import * as action from "@/store/users/action-types";
import * as mutation from "@/store/users/mutation-types";

export default {
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    },
    levyCollectionType: {
      type: String,
      required: true
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    state() {
      return this.$store.state.users.widgets;
    },
    widgets() {
      const current = _(this.state.data[this.levyCollectionType]).pluck("widget");
      let widgets = _.union(current, dashboardHelper.availableWidgets());
      widgets = _.map(widgets, w => {
        return {
          component: w,
          name: dashboardHelper.getWidgetName(w),
          description: dashboardHelper.getWidgetDescription(w),
          included: _.indexOf(current, w) >= 0
        };
      });
      return widgets;
    },
    includedWidgets() {
      return this.widgets.filter(w => w.included);
    },
    availableWidgets() {
      return this.widgets.filter(w => !w.included);
    }
  },
  methods: {
    hide() {
      this.$emit("update:show", false);
    },
    toggleIncluded(component) {
      const widget = _.find(this.widgets, w => w.component == component);
      this.$store.commit(widget.included ? mutation.WIDGET_REMOVE : mutation.WIDGET_ADD, {
        levyCollectionType: this.levyCollectionType,
        widget: component
      });
      this.save();
    },
    up(component) {
      this.$store.commit(mutation.WIDGET_MOVE_UP, { levyCollectionType: this.levyCollectionType, widget: component });
      this.save();
    },
    down(component) {
      this.$store.commit(mutation.WIDGET_MOVE_DOWN, { levyCollectionType: this.levyCollectionType, widget: component });
      this.save();
    },
    reset() {
      this.$store.commit(mutation.WIDGET_RESET, this.levyCollectionType);
      this.save();
      this.hide();
    },
    save() {
      this.$store.dispatch(action.WIDGETS_SAVE);
    }
  }
};
</script>
